import { Price } from "../services/searchService";

// Night window and state-based GST split mirror FusionCab's admin-side
// booking logic (fusion_admindash CreateBookingPage.tsx / backend
// calculationUtils.js) - 22:00-06:00 pickup counts as night, and GST is
// CGST+SGST 2.5% each for Telangana-origin trips, IGST 5% otherwise.
const NIGHT_START_HOUR = 22;
const NIGHT_END_HOUR = 6;
const CGST_SGST_RATE = 0.025;
const IGST_RATE = 0.05;
const HOME_STATE = "telangana";

export interface FareBreakdown {
  distanceKm: number;
  includedKm: number;
  extraKm: number;
  isNight: boolean;
  baseFare: number;
  extraFare: number;
  nightCharges: number;
  tollCharges: number;
  parkingCharges: number;
  driverCharges: number;
  waitingCharges: number;
  stateTax: number;
  greenTax: number;
  totalFareBeforeTax: number;
  sgst: number;
  cgst: number;
  igst: number;
  totalFare: number;
}

export function isNightTime(pickupTime: string): boolean {
  if (!pickupTime) return false;
  const hours = parseInt(pickupTime.split(":")[0], 10);
  if (isNaN(hours)) return false;
  return hours >= NIGHT_START_HOUR || hours < NIGHT_END_HOUR;
}

/**
 * price.fixed_fare is a per-km rate (confirmed by admin's own Price preview
 * calculator: fixed_fare * distance), applied to the plan's included/quoted
 * distance for a stable base fare; any distance beyond that (e.g. from a
 * live Google Maps route) is billed at price.extra_fare per km.
 */
export function calculateFare(
  price: Price,
  distanceKm: number | undefined,
  pickupTime: string,
  sourceState?: string
): FareBreakdown {
  const plan = price.Plan;
  const includedKm = plan.distance;
  const effectiveDistanceKm = distanceKm ?? includedKm;
  const extraKm = Math.max(0, effectiveDistanceKm - includedKm);

  const baseFare = price.fixed_fare * includedKm;
  const extraFare = extraKm * price.extra_fare;

  const night = isNightTime(pickupTime);
  const nightCharges = night
    ? price.night_base_fare + (plan.is_night ? plan.night_charges : 0)
    : 0;

  const tollCharges = plan.is_toll ? plan.toll_charges : 0;
  const parkingCharges = plan.is_parking ? plan.parking_charges : 0;
  const driverCharges = plan.is_driver ? plan.driver_charges : 0;
  const waitingCharges = plan.is_waiting ? plan.waiting_charges : 0;
  const stateTax = plan.is_tax ? plan.state_tax : 0;
  const greenTax = plan.is_tax ? plan.green_tax : 0;

  const totalFareBeforeTax =
    baseFare +
    extraFare +
    nightCharges +
    tollCharges +
    parkingCharges +
    driverCharges +
    waitingCharges +
    stateTax +
    greenTax;

  const isHomeState = (sourceState || "").trim().toLowerCase() === HOME_STATE;
  const sgst = isHomeState ? totalFareBeforeTax * CGST_SGST_RATE : 0;
  const cgst = isHomeState ? totalFareBeforeTax * CGST_SGST_RATE : 0;
  const igst = isHomeState ? 0 : totalFareBeforeTax * IGST_RATE;

  const totalFare = totalFareBeforeTax + sgst + cgst + igst;

  return {
    distanceKm: effectiveDistanceKm,
    includedKm,
    extraKm,
    isNight: night,
    baseFare,
    extraFare,
    nightCharges,
    tollCharges,
    parkingCharges,
    driverCharges,
    waitingCharges,
    stateTax,
    greenTax,
    totalFareBeforeTax,
    sgst,
    cgst,
    igst,
    totalFare,
  };
}
